import { Remove } from '@mui/icons-material'
import { IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import DateFormat from '../../../store/types/enums/DateFormat'
import { HoursInterval } from '../../../store/types'

type TimePickersRowProps = {
  index: number
  timePeriod: HoursInterval
  removePeriod: (id: number) => void
  changeStartTime: (id: number, time: string) => void
  changeEndTime: (id: number, time: string) => void
  isOverlapping: boolean
  length: number
}

const TimePickersRow = ({
  index,
  timePeriod,
  removePeriod,
  changeStartTime,
  changeEndTime,
  isOverlapping,
  length,
}: TimePickersRowProps) => {
  const id = +timePeriod.id

  const handleTimePeriodRemove = () => removePeriod(id)
  const handleStartTimeChange = (time: Dayjs | null) => changeStartTime(id, dayjs(time).format(DateFormat.HourMinute))
  const handleEndTimeChange = (time: Dayjs | null) => changeEndTime(id, dayjs(time).format(DateFormat.HourMinute))

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [fromError, setFromError] = useState(timePeriod.from === 'Invalid Date')
  const [toError, setToError] = useState(timePeriod.to === 'Invalid Date')

  useEffect(() => {
    setFromError(timePeriod.from === 'Invalid Date')
    setToError(timePeriod.to === 'Invalid Date')
  }, [timePeriod])

  return (
    <Stack key={`time-period-section-${timePeriod.id}`} gap={2} direction="row" alignItems="start" position="relative">
      {length !== 1 && (
        <IconButton
          size={'small'}
          onClick={handleTimePeriodRemove}
          aria-label={'remove-time-period-button'}
          sx={{
            left: isSm ? 0 : -30,
            width: 24,
            height: 24,
            visibility: index !== 0 ? 'visible' : 'hidden',
            position: isSm ? 'relative' : 'absolute',
            mt: 1,
          }}
        >
          <Remove />
        </IconButton>
      )}

      <Stack>
        <TimePicker
          label={t('components.table.cell.from')}
          value={dayjs(timePeriod.from, DateFormat.HourMinute)}
          ampm={false}
          slotProps={{
            textField: { size: 'small', error: isOverlapping || fromError },
          }}
          onChange={time => handleStartTimeChange(time)}
          sx={{
            svg: { color: 'text.mediumEmphasis' },
          }}
        />
        {fromError && (
          <Typography variant="caption" color="error.main" pt={0.5} pl={1}>
            {t('page.schedule.fillOutManyPopup.invalidTimePeriod')}
          </Typography>
        )}
      </Stack>

      <Stack>
        <TimePicker
          label={t('components.table.cell.to')}
          value={dayjs(timePeriod.to, DateFormat.HourMinute)}
          onChange={time => handleEndTimeChange(time)}
          ampm={false}
          minTime={dayjs(timePeriod.from, DateFormat.HourMinute)}
          sx={{
            svg: { color: 'text.mediumEmphasis' }
          }}
          slotProps={{
            textField: { size: 'small', error: isOverlapping || toError },
          }}
        />
        {toError && (
          <Typography variant="caption" color="error.main" pt={0.5} pl={1}>
            {t('page.schedule.fillOutManyPopup.invalidTimePeriod')}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default TimePickersRow
